/**
 * Sidebar Component
 * Collapsible side navigation menu with links to main sections
 */

import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { NAVIGATION_ITEMS, QUERY_KEYS } from "../../utils/constants";
import type { NavigationItem } from "../../utils/constants";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types/auth.types";
import { getOutstandingTasksCount } from "../../services/tasks.service";
import { isFeatureEnabled } from "../../utils/featureFlags";
import { useSmsConversationsAvailable } from "../../hooks/useSmsConversationsAvailable";
import { useFinanceAccess } from "../../hooks/useFinanceAccess";
import { useSchedulingDelegations } from "../../hooks/useSchedulingDelegations";
import {
  FIELD_MANAGER_NAV_PATHS,
  isFieldManagerRole,
} from "../../utils/fieldManagerAccess";
import type { FeatureFlags } from "../../utils/featureFlags";
import clsx from "clsx";
import {
  HomeIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  UsersIcon,
  UserGroupIcon,
  ListBulletIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  QrCodeIcon,
  TicketIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  GiftIcon,
  PhoneIcon,
  MegaphoneIcon,
  BoltIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  // Mobile off-canvas drawer: below `md` the sidebar is hidden by default and
  // slides in over the content instead of consuming layout width.
  mobileOpen: boolean;
  onMobileClose: () => void;
}

// True below Tailwind's `md` breakpoint (767px). Drives the off-canvas drawer:
// on mobile the sidebar always renders expanded (labels) and its width never
// consumes page width (it's `fixed` and slides in), regardless of the desktop
// collapse preference.
function useIsMobile(): boolean {
  const query = "(max-width: 767px)";
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// Map icon names to Heroicon components
const iconMap: Record<
  string,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  home: HomeIcon,
  "building-office": BuildingOfficeIcon,
  users: UsersIcon,
  "list-bullet": ListBulletIcon,
  "clipboard-document-list": ClipboardDocumentListIcon,
  calendar: CalendarIcon,
  "chat-bubble-left-right": ChatBubbleLeftRightIcon,
  "qr-code": QrCodeIcon,
  banknotes: BanknotesIcon,
  ticket: TicketIcon,
  "check-circle": CheckCircleIcon,
  "cog-6-tooth": Cog6ToothIcon,
  "exclamation-triangle": ExclamationTriangleIcon,
  "document-duplicate": DocumentDuplicateIcon,
  gift: GiftIcon,
  phone: PhoneIcon,
  megaphone: MegaphoneIcon,
  bolt: BoltIcon,
  "user-group": UserGroupIcon,
  "document-text": DocumentTextIcon,
  "chart-bar": ChartBarIcon,
  "clipboard-document-check": ClipboardDocumentCheckIcon,
  "calendar-days": CalendarDaysIcon,
  "table-cells": TableCellsIcon,
  sparkles: SparklesIcon,
};

// OI-05 (2026-06-20): a collapsible nav group (e.g. "Sales Team"). Expand state
// persists per group in localStorage; default is expanded. When the whole
// sidebar is collapsed (icon-only), there's no room for an expandable label, so
// the group's children render as flat icon links instead.
const NavGroup: React.FC<{
  item: NavigationItem;
  collapsed: boolean;
  renderChild: (child: NavigationItem) => React.ReactNode;
}> = ({ item, collapsed, renderChild }) => {
  const storageKey = `sidebar-group:${item.name}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  });
  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        /* ignore storage failures */
      }
      return next;
    });
  };
  const Icon = iconMap[item.icon || "home"];
  const children = item.children ?? [];

  if (collapsed) {
    // Icon-only mode has no room for the "Soon" badge/label, so a placeholder
    // would render as a dead-looking icon — drop placeholders here; real links
    // still show as flat icons.
    return <>{children.filter((c) => !c.placeholder).map(renderChild)}</>;
  }

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
      >
        <div className="flex items-center space-x-3">
          {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
          <span className="font-medium">{item.name}</span>
        </div>
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4" />
        ) : (
          <ChevronRightIcon className="h-4 w-4" />
        )}
      </button>
      {expanded && (
        <div className="mt-1 ml-3 pl-3 border-l border-gray-800 space-y-1">
          {children.map(renderChild)}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed: collapsedProp,
  onToggle,
  mobileOpen,
  onMobileClose,
}) => {
  const { user } = useAuth();
  // mig 1780: an MSR named scheduling delegate for a team also gets the two scheduling pages
  const { isDelegate: isSchedulingDelegate } = useSchedulingDelegations();
  const isMobile = useIsMobile();
  // On mobile the drawer is always fully expanded (labels shown); the desktop
  // icon-only collapse never applies. All content + width logic below reads this
  // effective value, so shadowing the prop keeps the rest of the component intact.
  const collapsed = isMobile ? false : collapsedProp;

  // Keep the off-screen (closed) mobile drawer out of the tab order / a11y tree.
  // `inert` is toggled imperatively (React 18 lacks JSX typing for it); browsers
  // without `inert` support degrade gracefully to the aria-hidden below.
  const asideRef = React.useRef<HTMLElement>(null);
  const drawerHidden = isMobile && !mobileOpen;
  React.useEffect(() => {
    const el = asideRef.current;
    if (!el) {
      return;
    }
    if (drawerHidden) {
      el.setAttribute("inert", "");
    } else {
      el.removeAttribute("inert");
    }
  }, [drawerHidden]);

  // Fetch outstanding tasks count for the current user
  const { data: taskCount = 0 } = useQuery({
    queryKey: [QUERY_KEYS.TASKS, "outstanding", user?.id],
    queryFn: () => getOutstandingTasksCount(user!.id),
    enabled: !!user?.id,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider stale after 2 minutes
  });

  // Normalize role for comparison (handles SUPERADMIN, super_admin, etc.)
  const normalizeRole = (role: string): string => {
    return role.toLowerCase().replace(/[_-]/g, "");
  };

  // Support role navigation items (filter by feature flags)
  const SUPPORT_NAV_ITEMS = [
    { name: "Organizations", path: "/organizations", icon: "building-office" },
    { name: "Contacts", path: "/contacts", icon: "users" },
    {
      name: "Support Tasks",
      path: "/support-tasks",
      icon: "phone",
      featureFlag: "supportTasksDashboard" as keyof FeatureFlags,
    },
    { name: "Events", path: "/events", icon: "calendar" },
    {
      name: "Interactions",
      path: "/interactions",
      icon: "chat-bubble-left-right",
    },
    { name: "Redemptions", path: "/redemptions", icon: "check-circle" },
    {
      name: "Attorney Questions",
      path: "/attorney-questions",
      icon: "scale",
      featureFlag: "attorneyQuestionRouting" as keyof FeatureFlags,
    },
    // The support roles get this hard-coded list rather than NAVIGATION_ITEMS,
    // so the "What's New" link has to be added here too or they would have no
    // way to reach a page the database already lets them read.
    { name: "What's New", path: "/whats-new", icon: "sparkles" },
  ].filter((item) => !item.featureFlag || isFeatureEnabled(item.featureFlag));

  // R8-002 (2026-06-20): a field MSR is locked to their own mobile portal. The
  // sidebar shows ONLY their pages + booth + commission — no admin nav. (Most
  // admin routes already exclude `msr` via RequireRole; this removes the links
  // too so the field experience is the portal, not the admin app.)
  const MSR_NAV_ITEMS: NavigationItem[] = [
    { name: "My Schedule", path: "/msr", icon: "calendar" },
    {
      name: "My Availability",
      path: "/msr/availability",
      icon: "calendar-days",
    },
    { name: "Sales History", path: "/msr/sales-history", icon: "chart-bar" },
    {
      name: "Commission Calculator",
      path: "/commission-estimator",
      icon: "chart-bar",
    },
    // "Booth Check-in" / "Event Check-in" links removed 2026-08 (login-gated + confusing;
    // public check-in is /self-checkin, assisted check-in is per-event). Routes still exist.
    // What's New removed for MSRs (MSR QA notes 2026-10-19, item 10): release notes are written for office staff,
    // not field reps. The route refuses the msr role too.
  ];

  const READONLY_NAV_ITEMS: NavigationItem[] = [
    { name: "Contacts", path: "/contacts", icon: "users" },
    { name: "Organizations", path: "/organizations", icon: "building-office" },
    { name: "What's New", path: "/whats-new", icon: "sparkles" },
  ];

  // Filter navigation items based on user role
  const visibleNavItems = useMemo<NavigationItem[]>(() => {
    if (user?.role === UserRole.MSR) {
      // Field reps see only their portal (R8-002 lockdown) -- plus, for a scheduling delegate, the team's Sales
      // Calendar and availability (the database scopes both to the team they schedule for).
      return isSchedulingDelegate
        ? [
            ...MSR_NAV_ITEMS,
            {
              name: "Sales Calendar",
              path: "/sales-calendar",
              icon: "calendar-days",
            },
            {
              name: "Team Availability",
              path: "/msr-team?tab=availability",
              icon: "user-group",
            },
          ]
        : MSR_NAV_ITEMS;
    }

    if (user?.role === UserRole.PARTNER) {
      // Partners (external organisations) only see redemptions -- and NOT What's New: release
      // notes describe internal operations (the database refuses partners the rows too).
      return [
        {
          name: "Redemptions",
          path: "/redemptions",
        },
      ];
    }

    if (user?.role === UserRole.FINANCE) {
      // Finance sees redemptions, plus What's New (owner 2026-09-27: every user except partners).
      return [
        {
          name: "Redemptions",
          path: "/redemptions",
        },
        { name: "What's New", path: "/whats-new", icon: "sparkles" },
      ];
    }

    if (user?.role === UserRole.READONLY) {
      // Read-only viewer (owner 2026-11-03): verify membership and partner agreements -- view only. A fixed list, so a
      // module added later never appears for this role by default.
      return READONLY_NAV_ITEMS;
    }

    if (
      user?.role === UserRole.SUPPORT_ADMIN ||
      user?.role === UserRole.SUPPORT_AGENT
    ) {
      // Support roles see limited navigation with Support Tasks first
      return SUPPORT_NAV_ITEMS;
    }

    // Other users see navigation items based on their role and feature flags.
    const normalizedUserRole = normalizeRole(user?.role || "");
    // Regional / Territory Managers (owner 2026-10-29): only the modules they use. A curated allow-list on top of the
    // usual role / flag checks, so a module added later never shows up for them by default.
    const fieldManager = isFieldManagerRole(user?.role);
    const allowedForFieldManager = (item: NavigationItem): boolean =>
      !fieldManager ||
      (item.path
        ? FIELD_MANAGER_NAV_PATHS.has(item.path)
        : !!item.placeholder || !!item.children);
    const isVisible = (item: NavigationItem): boolean => {
      // Check feature flag first - if feature is disabled, hide the item
      if (
        item.featureFlag &&
        !isFeatureEnabled(item.featureFlag as keyof FeatureFlags)
      ) {
        return false;
      }
      // Multi-role gate (audit fix): when an item lists requiredRoles, show it
      // only to those roles (matching the route's allowedRoles) so a link can't
      // appear for a role the route then denies.
      const requiredRoles = item.requiredRoles;
      if (requiredRoles && requiredRoles.length > 0) {
        return requiredRoles.some(
          (r) => normalizeRole(r) === normalizedUserRole,
        );
      }
      // If no required role, show to everyone
      if (!item.requiredRole) {
        return true;
      }
      // Check if user has the required role (with normalization)
      return normalizedUserRole === normalizeRole(item.requiredRole);
    };

    // OI-05: a group (item.children) is kept only if at least one child is
    // visible to this user; its child list is filtered to the visible ones.
    const show = (item: NavigationItem): boolean =>
      isVisible(item) && allowedForFieldManager(item);
    return NAVIGATION_ITEMS.map((item) => {
      if (item.children) {
        if (!show(item)) {
          return null;
        }
        const kids = item.children.filter(show);
        return kids.length > 0 ? { ...item, children: kids } : null;
      }
      return show(item) ? item : null;
    }).filter((item): item is NavigationItem => item !== null);
  }, [user?.role, isSchedulingDelegate]);

  // Two-way SMS inbox: shown ONLY when the server probe answers true (never on false, loading or
  // error). Inserted after Contacts; the hook never asks for roles the server would refuse.
  const { available: smsInboxAvailable } = useSmsConversationsAvailable();
  // Finance (expenses + approvals): shown ONLY when the server says the finance module is on and this person is
  // allowed (finance_my_access): Finance, Admin, Superadmin, and approvers of any other staff role (never MSRs --
  // migration 1140), so the link is appended to whichever list this role gets.
  const { visible: financeVisible } = useFinanceAccess(null);
  const isReadonlyViewer = user?.role === UserRole.READONLY;
  const baseNavItems = useMemo<NavigationItem[]>(
    () =>
      !isReadonlyViewer &&
      financeVisible &&
      !visibleNavItems.some((i) => i.path === "/finance")
        ? [
            ...visibleNavItems,
            { name: "Finance", path: "/finance", icon: "banknotes" },
          ]
        : visibleNavItems,
    [visibleNavItems, financeVisible, isReadonlyViewer],
  );
  const navItems = useMemo<NavigationItem[]>(() => {
    const visibleNavItems = baseNavItems;
    if (!smsInboxAvailable || isReadonlyViewer) {
      return visibleNavItems;
    }
    const messages: NavigationItem = {
      name: "SMS inbox",
      path: "/messages",
      icon: "chat-bubble-left-right",
    };
    const at = visibleNavItems.findIndex((i) => i.path === "/contacts");
    return at >= 0
      ? [
          ...visibleNavItems.slice(0, at + 1),
          messages,
          ...visibleNavItems.slice(at + 1),
        ]
      : [...visibleNavItems, messages];
  }, [baseNavItems, smsInboxAvailable, isReadonlyViewer]);

  // Get badge count for a navigation item
  const getBadgeCount = (path: string): number => {
    if (path === "/tasks") {
      return taskCount;
    }
    return 0;
  };

  // Render a single (non-group) nav entry: a placeholder is a disabled "Soon"
  // row; everything else is a NavLink. Used for top-level items and group
  // children alike (children are visually indented by their group container).
  const renderNavItem = (item: NavigationItem): React.ReactNode => {
    const Icon = iconMap[item.icon || "home"];

    if (item.placeholder) {
      return (
        <div
          key={item.name}
          title="Coming soon"
          className={clsx(
            "flex items-center rounded-lg relative cursor-default text-gray-500",
            collapsed
              ? "justify-center px-2 py-3"
              : "justify-between px-4 py-3",
          )}
        >
          <div
            className={clsx("flex items-center", collapsed ? "" : "space-x-3")}
          >
            {Icon && <Icon className="h-5 w-5 flex-shrink-0 opacity-60" />}
            {!collapsed && <span className="font-medium">{item.name}</span>}
          </div>
          {!collapsed && (
            <span className="text-[10px] uppercase tracking-wide bg-gray-700 text-gray-300 rounded px-1.5 py-0.5">
              Soon
            </span>
          )}
        </div>
      );
    }

    const path = item.path ?? "#";
    const badgeCount = getBadgeCount(path);
    return (
      <NavLink
        key={path}
        to={path}
        title={collapsed ? item.name : undefined}
        onClick={() => {
          if (isMobile) {
            onMobileClose();
          }
        }}
        className={({ isActive }) =>
          clsx(
            "flex items-center rounded-lg transition-colors relative",
            collapsed
              ? "justify-center px-2 py-3"
              : "justify-between px-4 py-3",
            {
              "bg-primary-600 text-white": isActive,
              "text-gray-300 hover:bg-gray-800 hover:text-white": !isActive,
            },
          )
        }
      >
        <div
          className={clsx("flex items-center", collapsed ? "" : "space-x-3")}
        >
          {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
          {!collapsed && <span className="font-medium">{item.name}</span>}
        </div>
        {badgeCount > 0 && (
          <span
            className={clsx(
              "inline-flex items-center justify-center text-xs font-medium bg-red-500 text-white rounded-full min-w-[20px]",
              collapsed
                ? "absolute -top-1 -right-1 px-1 py-0 text-[10px]"
                : "px-2 py-0.5",
            )}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      ref={asideRef}
      aria-hidden={drawerHidden || undefined}
      className={clsx(
        "bg-gray-900 min-h-screen transition-all duration-200 flex flex-col",
        collapsed ? "w-16" : "w-64",
        // Mobile: off-canvas drawer over the content (never consumes page width);
        // slides in when open. Desktop (md+): normal in-flow column.
        isMobile
          ? clsx(
              "fixed inset-y-0 left-0 z-40 shadow-xl",
              mobileOpen ? "translate-x-0" : "-translate-x-full",
            )
          : "translate-x-0",
      )}
    >
      {/* Toggle header */}
      <div
        className={clsx(
          "flex items-center border-b border-gray-800",
          collapsed ? "justify-center px-2 py-3" : "justify-between px-4 py-3",
        )}
      >
        {!collapsed && (
          <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
            Menu
          </span>
        )}
        {/* On mobile the header button closes the drawer; on desktop it toggles
            the icon-only collapse. */}
        <button
          onClick={isMobile ? onMobileClose : onToggle}
          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          title={
            isMobile
              ? "Close menu"
              : collapsed
                ? "Expand menu"
                : "Collapse menu"
          }
        >
          {isMobile ? (
            <XMarkIcon className="h-5 w-5" />
          ) : collapsed ? (
            <Bars3Icon className="h-5 w-5" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      <nav
        className={clsx(
          "flex-1 space-y-1 overflow-y-auto",
          collapsed ? "p-2" : "p-4",
        )}
      >
        {navItems.map((item) =>
          item.children ? (
            <NavGroup
              key={item.name}
              item={item}
              collapsed={collapsed}
              renderChild={renderNavItem}
            />
          ) : (
            renderNavItem(item)
          ),
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
